import React, { useEffect, useRef, useState } from "react";
import { addTeacher, bulkAddTeachers, deleteTeacher, getSubjects, getTeachers, updateTeacher } from "../services/api";
import DateInput from "./DateInput";
import { downloadCsvTemplate, readCsvFile } from "../utils/bulkImport";
import { confirmAppAction, showAppToast } from "../utils/feedback";
import { formatDate, toInputDate } from "../utils/helpers";
import "./TeacherList.css";

const emptyTeacherForm = {
  name: "",
  subject: "",
  phone: "",
  experience: "",
  dob: "",
  schoolName: "",
  joiningDate: "",
  imageData: ""
};

const readJpegFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg"];
    const lowerName = file.name.toLowerCase();
    const hasValidExtension = lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg");

    if (!allowedTypes.includes(file.type) && !hasValidExtension) {
      reject(new Error("Only JPG and JPEG images are allowed"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });

function TeacherList({ searchSelection, onOpenProfile }) {
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [viewMode, setViewMode] = useState("card");
  const [formData, setFormData] = useState(emptyTeacherForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [bulkFileKey, setBulkFileKey] = useState(0);
  const formSectionRef = useRef(null);

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    fetchTeachers();
  }, [selectedSubject]);

  useEffect(() => {
    if (searchSelection?.type === "teacher") {
      setSelectedSubject(searchSelection.filterValue || "all");
      setViewMode("detail");
    }
  }, [searchSelection]);

  useEffect(() => {
    if (searchSelection?.type === "teacher" && searchSelection.action === "edit" && teachers.length > 0) {
      const matchedTeacher = teachers.find((teacher) => teacher._id === searchSelection.id);
      if (matchedTeacher) {
        handleEditTeacher(matchedTeacher);
      }
    }
  }, [searchSelection, teachers]);

  useEffect(() => {
    if (!showForm) {
      return;
    }

    const timer = window.setTimeout(() => {
      formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [showForm, editingId]);

  const fetchSubjects = () => {
    getSubjects()
      .then((res) => setSubjects(res.data))
      .catch((err) => console.error("Error fetching subjects:", err));
  };

  const fetchTeachers = () => {
    getTeachers(selectedSubject, { includeImages: true })
      .then((res) => setTeachers(res.data))
      .catch((err) => console.error("Error fetching teachers:", err));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];

    try {
      const imageData = await readJpegFile(file);
      setFormData({ ...formData, imageData });
    } catch (err) {
      showAppToast({ type: "error", title: "Image upload failed", message: err.message });
      setFileInputKey((current) => current + 1);
    }
  };

  const resetForm = () => {
    setFormData(emptyTeacherForm);
    setEditingId(null);
    setShowForm(false);
    setFileInputKey((current) => current + 1);
  };

  const handleDownloadTemplate = () => {
    downloadCsvTemplate(
      "teachers-bulk-template.csv",
      ["name", "subject", "phone", "experience", "dob", "schoolName", "joiningDate"],
      [
        {
          name: "Anita Verma",
          subject: "Mathematics",
          phone: "9876500001",
          experience: "5 years",
          dob: "15-02-1990",
          schoolName: "Bright Line Classes",
          joiningDate: "01-04-2026"
        },
        {
          name: "Rohit Sen",
          subject: "Science",
          phone: "9876500002",
          experience: "3 years",
          dob: "20-06-1992",
          schoolName: "Bright Line Classes",
          joiningDate: "05-04-2026"
        }
      ]
    );
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const rows = await readCsvFile(file);
      const teachersPayload = rows.map((row) => ({
        name: row.name,
        subject: row.subject,
        phone: row.phone,
        experience: row.experience,
        dob: toInputDate(row.dob),
        schoolName: row.schoolName,
        joiningDate: toInputDate(row.joiningDate)
      }));

      if (teachersPayload.length === 0) {
        showAppToast({
          type: "error",
          title: "No teachers found",
          message: "The uploaded file does not contain any teacher rows."
        });
        return;
      }

      await bulkAddTeachers({ teachers: teachersPayload });
      showAppToast({
        type: "success",
        title: "Teachers uploaded",
        message: `${teachersPayload.length} teacher records were added successfully.`
      });
      fetchTeachers();
      fetchSubjects();
      setBulkFileKey((current) => current + 1);
    } catch (err) {
      console.error("Bulk upload failed:", err);
      showAppToast({
        type: "error",
        title: "Upload failed",
        message: "Please use the sample template columns for the teacher file."
      });
      setBulkFileKey((current) => current + 1);
    }
  };

  const handleAddTeacher = async () => {
    if (!formData.name || !formData.subject || !formData.phone || !formData.experience) {
      showAppToast({
        type: "error",
        title: "Missing details",
        message: "Please fill in all required teacher fields before saving."
      });
      return;
    }

    const payload = {
      name: formData.name,
      subject: formData.subject,
      phone: formData.phone,
      experience: formData.experience,
      dob: formData.dob,
      schoolName: formData.schoolName,
      joiningDate: formData.joiningDate,
      imageData: formData.imageData
    };

    try {
      if (editingId) {
        await updateTeacher(editingId, payload);
        showAppToast({
          type: "success",
          title: "Teacher updated",
          message: `${formData.name} was updated successfully.`
        });
      } else {
        await addTeacher(payload);
        showAppToast({
          type: "success",
          title: "Teacher added",
          message: `${formData.name} was added successfully.`
        });
      }

      resetForm();
      fetchTeachers();
      fetchSubjects();
    } catch (err) {
      console.error("Error saving teacher:", err);
      showAppToast({
        type: "error",
        title: "Failed to save teacher",
        message: err.response?.data?.error || err.message
      });
    }
  };

  const handleEditTeacher = (teacher) => {
    setFormData({
      name: teacher.name || "",
      subject: teacher.subject || "",
      phone: teacher.phone || "",
      experience: teacher.experience || "",
      dob: toInputDate(teacher.dob),
      schoolName: teacher.schoolName || "",
      joiningDate: toInputDate(teacher.joiningDate),
      imageData: teacher.imageData || teacher.imageUrl || ""
    });
    setEditingId(teacher._id);
    setShowForm(true);
    setFileInputKey((current) => current + 1);
  };

  const handleDeleteTeacher = async (id) => {
    const targetTeacher = teachers.find((teacher) => teacher._id === id);
    const confirmed = await confirmAppAction({
      title: "Delete teacher",
      message: `This will permanently remove ${targetTeacher?.name || "this teacher"} from the system.`,
      confirmLabel: "Delete"
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteTeacher(id);
      showAppToast({
        type: "success",
        title: "Teacher deleted",
        message: `${targetTeacher?.name || "The teacher"} was removed successfully.`
      });
      fetchTeachers();
      fetchSubjects();
    } catch (err) {
      console.error("Error deleting teacher:", err);
      showAppToast({
        type: "error",
        title: "Failed to delete teacher",
        message: err.response?.data?.error || err.message || "The teacher could not be removed."
      });
    }
  };

  const totalTeachers = teachers.length;
  const openProfile = (teacher) => {
    if (!onOpenProfile) {
      return;
    }

    onOpenProfile({
      id: teacher._id,
      type: "teacher",
      title: teacher.name,
      subtitle: `${teacher.subject} • ${teacher.experience}`,
      meta: teacher.phone,
      filterValue: teacher.subject
    });
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Teachers Dashboard</h1>
        {/* <p>Manage teachers in a card view and upload JPG/JPEG profile photos.</p> */}
      </div>

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Teachers in {selectedSubject === "all" ? "All Subjects" : selectedSubject}</h3>
          <p className="stat-number">{totalTeachers}</p>
        </div>
        <div className="stat-card">
          <h3>Active Teachers</h3>
          <p className="stat-number">{totalTeachers}</p>
        </div>
        <div className="stat-card">
          <h3>Subjects</h3>
          <p className="stat-number">{new Set(teachers.map((teacher) => teacher.subject)).size}</p>
        </div>
      </div>

      <div className="filter-section">
        <div className="toolbar-group toolbar-group--left">
          <div className="filter-container filter-container--compact">
            <label htmlFor="subject-select" className="filter-label">Subject</label>
            <select
              id="subject-select"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="subject-select"
            >
              <option value="all">All Subjects</option>
              {subjects.map((subject) => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>
          {!showForm && (
            <div className="bulk-tools">
              <button className="btn btn-success btn--compact" onClick={() => setShowForm(true)}>
                Add Teacher
              </button>
              <button className="btn btn-secondary btn--compact" onClick={handleDownloadTemplate}>
                Sample Excel
              </button>
              <label className="btn btn-primary btn--compact bulk-tools__upload">
                Bulk Upload
                <input
                  key={bulkFileKey}
                  type="file"
                  accept=".csv"
                  onChange={handleBulkUpload}
                />
              </label>
            </div>
          )}
        </div>
        <div className="toolbar-group toolbar-group--right">
          <div className="view-toggle">
            <span className="view-toggle__label">View</span>
            <button
              className={`view-toggle__btn ${viewMode === "card" ? "active" : ""}`}
              onClick={() => setViewMode("card")}
            >
              Card
            </button>
            <button
              className={`view-toggle__btn ${viewMode === "detail" ? "active" : ""}`}
              onClick={() => setViewMode("detail")}
            >
              Detail
            </button>
          </div>
        </div>
      </div>

      <div className="main-content">
        {showForm && (
          <div className="form-section" ref={formSectionRef}>
            <h3>{editingId ? "Edit Teacher" : "Add New Teacher"}</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Teacher Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter teacher name" />
              </div>
              <div className="form-group">
                <label>Subject</label>
                <input type="text" name="subject" value={formData.subject} onChange={handleInputChange} placeholder="e.g., Mathematics" />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="9876500001" />
              </div>
              <div className="form-group">
                <label>Experience</label>
                <input type="text" name="experience" value={formData.experience} onChange={handleInputChange} placeholder="e.g., 5 years" />
              </div>
              <div className="form-group">
                <label>DOB</label>
                <DateInput name="dob" value={formData.dob} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>School Name</label>
                <input type="text" name="schoolName" value={formData.schoolName} onChange={handleInputChange} placeholder="Enter school or institute name" />
              </div>
              <div className="form-group">
                <label>Joining Date</label>
                <DateInput name="joiningDate" value={formData.joiningDate} onChange={handleInputChange} />
              </div>
              <div className="form-group form-group--full">
                <label>Teacher Image</label>
                <input
                  key={fileInputKey}
                  type="file"
                  accept=".jpg,.jpeg,image/jpeg"
                  onChange={handleImageChange}
                />
                <small className="upload-help">Only JPG and JPEG files are supported.</small>
              </div>
              {formData.imageData && (
                <div className="form-group form-group--full">
                  <img src={formData.imageData} alt="Teacher preview" className="form-preview-image" />
                </div>
              )}
            </div>
            <div className="button-group">
              <button className="btn btn-primary" onClick={handleAddTeacher}>
                {editingId ? "Update Teacher" : "Add Teacher"}
              </button>
              <button className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {teachers.length === 0 && <p className="empty-state">No teachers found for the selected subject.</p>}

        {teachers.length > 0 && viewMode === "card" && (
          <div className="card-grid">
            {teachers.map((teacher) => {
              const imageSource = teacher.imageData || teacher.imageUrl;
              return (
                <article
                  className={`profile-card ${searchSelection?.id === teacher._id ? "profile-card--highlighted" : ""}`}
                  key={teacher._id}
                >
                  <button className="profile-card__media profile-card__media-btn" onClick={() => openProfile(teacher)}>
                    {imageSource ? (
                      <img src={imageSource} alt={teacher.name} className="profile-card__image" />
                    ) : (
                      <div className="profile-card__placeholder">
                        {teacher.name?.slice(0, 1)?.toUpperCase() || "T"}
                      </div>
                    )}
                  </button>
                  <div className="profile-card__body">
                    <div className="profile-card__heading">
                      <button className="profile-card__name-btn" onClick={() => openProfile(teacher)}>
                        {teacher.name}
                      </button>
                      <span className="subject-badge">{teacher.subject}</span>
                    </div>
                    <p><strong>Phone:</strong> {teacher.phone}</p>
                    <p><strong>Experience:</strong> {teacher.experience}</p>
                    <p><strong>DOB:</strong> {formatDate(teacher.dob)}</p>
                    {teacher.schoolName && <p><strong>School:</strong> {teacher.schoolName}</p>}
                    <p><strong>Joining:</strong> {formatDate(teacher.joiningDate)}</p>
                  </div>
                  <div className="profile-card__actions">
                    <button className="action-btn edit" onClick={() => handleEditTeacher(teacher)}>Edit</button>
                    <button className="action-btn delete" onClick={() => handleDeleteTeacher(teacher._id)}>Delete</button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {teachers.length > 0 && viewMode === "detail" && (
          <div className="detail-table-wrap">
            <table className="detail-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Subject</th>
                  <th>Phone</th>
                  <th>Experience</th>
                  <th>DOB</th>
                  <th>School Name</th>
                  <th>Joining Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher) => {
                  const imageSource = teacher.imageData || teacher.imageUrl;
                  return (
                    <tr
                      key={teacher._id}
                      className={searchSelection?.id === teacher._id ? "detail-table__row--highlighted" : ""}
                    >
                      <td>
                        {imageSource ? (
                          <button className="detail-table__avatar-btn" onClick={() => openProfile(teacher)}>
                            <img src={imageSource} alt={teacher.name} className="detail-table__avatar" />
                          </button>
                        ) : (
                          <button className="detail-table__avatar-btn" onClick={() => openProfile(teacher)}>
                            <div className="detail-table__placeholder">
                              {teacher.name?.slice(0, 1)?.toUpperCase() || "T"}
                            </div>
                          </button>
                        )}
                      </td>
                      <td>
                        <button className="detail-table__name-btn" onClick={() => openProfile(teacher)}>
                          {teacher.name}
                        </button>
                      </td>
                      <td><span className="subject-badge">{teacher.subject}</span></td>
                      <td>{teacher.phone}</td>
                      <td>{teacher.experience}</td>
                      <td>{formatDate(teacher.dob)}</td>
                      <td>{teacher.schoolName || "-"}</td>
                      <td>{formatDate(teacher.joiningDate)}</td>
                      <td>
                        <div className="detail-table__actions">
                          <button className="action-btn edit" onClick={() => handleEditTeacher(teacher)}>Edit</button>
                          <button className="action-btn delete" onClick={() => handleDeleteTeacher(teacher._id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default TeacherList;
